import { apiClient } from '../../../lib/api/client';
import {
  normalizeLeadPipelineResponse,
  normalizeLeadSource,
} from '../leadMetadata';
import type {
  LeadListItem,
  LeadKanbanItem,
  LeadDetails,
  LeadFormValues,
  LeadActivityFormValues,
  ExportLeadsParams,
  GetLeadsParams,
  CloseLeadPayload,
  PagedResult,
  LeadPipelineResponse,
  LeadSourceDefinition,
} from '../types';

export async function getLeads(
  params: GetLeadsParams = {}
): Promise<PagedResult<LeadListItem>> {
  const {
    pageNumber = 1,
    pageSize = 10,
    search,
    pipelineStage,
    pipelineStageId,
    closingStatus,
    assigneeUserId,
  } = params;

  const searchParams = new URLSearchParams();
  searchParams.set('pageNumber', String(pageNumber));
  searchParams.set('pageSize', String(pageSize));
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (pipelineStageId !== undefined) searchParams.set('pipelineStageId', String(pipelineStageId));
  else if (pipelineStage !== undefined) searchParams.set('pipelineStage', String(pipelineStage));
  if (closingStatus !== undefined) searchParams.set('closingStatus', String(closingStatus));
  if (assigneeUserId !== undefined && assigneeUserId !== '')
    searchParams.set('assigneeUserId', assigneeUserId);
  if (params.contactCategoryId !== undefined)
    searchParams.set('contactCategoryId', String(params.contactCategoryId));
  if (params.sourceId !== undefined) searchParams.set('sourceId', String(params.sourceId));
  if (params.campaignId !== undefined && params.campaignId !== '')
    searchParams.set('campaignId', params.campaignId);
  if (params.contactIds !== undefined && params.contactIds.length > 0) {
    for (const id of params.contactIds) searchParams.append('contactIds', id);
  }
  if (params.referralContactId !== undefined && params.referralContactId !== '')
    searchParams.set('referralContactId', params.referralContactId);
  if (params.salesPersonId !== undefined && params.salesPersonId !== '')
    searchParams.set('salesPersonId', params.salesPersonId);
  if (params.createdFrom) searchParams.set('createdFrom', params.createdFrom);
  if (params.createdTo) searchParams.set('createdTo', params.createdTo);
  if (params.segmentIds?.length) {
    for (const id of params.segmentIds) {
      searchParams.append('segmentIds', id);
    }
  }
  if (params.hasReferral !== undefined) {
    searchParams.set('hasReferral', params.hasReferral ? 'true' : 'false');
  }

  const response = await apiClient.get<PagedResult<LeadListItem>>(
    `/leads?${searchParams.toString()}`
  );
  return response.data;
}

export async function getLeadsKanban(params?: {
  sourceId?: number;
  campaignId?: string;
  contactCategoryId?: number;
  contactIds?: string[];
  segmentIds?: string[];
}): Promise<LeadKanbanItem[]> {
  const searchParams = new URLSearchParams();
  if (params?.sourceId !== undefined) searchParams.set('sourceId', String(params.sourceId));
  if (params?.campaignId !== undefined && params.campaignId !== '')
    searchParams.set('campaignId', params.campaignId);
  if (params?.contactCategoryId !== undefined)
    searchParams.set('contactCategoryId', String(params.contactCategoryId));
  if (params?.contactIds !== undefined && params.contactIds.length > 0) {
    for (const id of params.contactIds) searchParams.append('contactIds', id);
  }
  if (params?.segmentIds?.length) {
    for (const id of params.segmentIds) {
      searchParams.append('segmentIds', id);
    }
  }
  const query = searchParams.toString();
  const url = query ? `/leads/kanban?${query}` : '/leads/kanban';
  const response = await apiClient.get<LeadKanbanItem[]>(url);
  return response.data;
}

export async function getLeadById(id: string): Promise<LeadDetails> {
  const response = await apiClient.get<LeadDetails>(`/leads/${id}`);
  return response.data;
}

export async function createLead(values: LeadFormValues): Promise<{ id: string }> {
  const response = await apiClient.post<{ id: string }>('/leads', {
    title: values.title,
    description: values.description ?? null,
    contactId: values.contactId,
    offerings: values.offerings,
    assigneeUserId: values.assigneeUserId,
    prospectingDate: values.prospectingDate ?? null,
    estimatedClosingDate: values.estimatedClosingDate ?? null,
    budgetScore: values.budgetScore ?? 0,
    authorityScore: values.authorityScore ?? 0,
    needScore: values.needScore ?? 0,
    timelineScore: values.timelineScore ?? 0,
    pipelineStage: values.pipelineStage ?? 1,
    pipelineStageId: values.pipelineStageId ?? null,
    sourceId: values.sourceId,
    campaignId: values.campaignId ?? null,
    referralContactId: values.referralContactId ?? null,
    salesPersonId: values.salesPersonId ?? null,
  });
  return response.data;
}

export async function updateLead(id: string, values: LeadFormValues): Promise<void> {
  await apiClient.put(`/leads/${id}`, {
    title: values.title,
    description: values.description ?? null,
    contactId: values.contactId,
    offerings: values.offerings,
    assigneeUserId: values.assigneeUserId,
    prospectingDate: values.prospectingDate ?? null,
    estimatedClosingDate: values.estimatedClosingDate ?? null,
    budgetScore: values.budgetScore ?? 0,
    authorityScore: values.authorityScore ?? 0,
    needScore: values.needScore ?? 0,
    timelineScore: values.timelineScore ?? 0,
    pipelineStage: values.pipelineStage ?? 1,
    pipelineStageId: values.pipelineStageId ?? null,
    sourceId: values.sourceId,
    campaignId: values.campaignId ?? null,
    referralContactId: values.referralContactId ?? null,
    salesPersonId: values.salesPersonId ?? null,
  });
}

export async function updateLeadBant(
  id: string,
  payload: {
    budgetScore: number;
    authorityScore: number;
    needScore: number;
    timelineScore: number;
  }
): Promise<void> {
  await apiClient.patch(`/leads/${id}/bant`, payload);
}

export async function changeLeadStage(
  id: string,
  payload: {
    toStageId: string;
    expectedVersion?: number;
    closedAmount?: number;
    actualClosingDate?: string;
    closingNote?: string;
    scheduleNext?: boolean;
    nextDueDate?: string;
    nextTitle?: string;
    nextDescription?: string;
    nextPriority?: number;
  }
): Promise<void> {
  await apiClient.patch(`/leads/${id}/stage`, {
    toStageId: payload.toStageId,
    expectedVersion: payload.expectedVersion ?? null,
    closedAmount: payload.closedAmount ?? null,
    actualClosingDate: payload.actualClosingDate ?? null,
    closingNote: payload.closingNote ?? null,
    scheduleNext: payload.scheduleNext ?? false,
    nextDueDate: payload.nextDueDate ?? null,
    nextTitle: payload.nextTitle ?? null,
    nextDescription: payload.nextDescription ?? null,
    nextPriority: payload.nextPriority ?? null,
  });
}

export async function addLeadActivity(
  id: string,
  values: LeadActivityFormValues
): Promise<void> {
  await apiClient.post(`/leads/${id}/activities`, {
    activityType: values.activityType,
    date: values.date ?? null,
    note: values.note ?? null,
  });
}

export async function closeLead(id: string, payload: CloseLeadPayload): Promise<void> {
  await apiClient.patch(`/leads/${id}/close`, {
    closingStatus: payload.closingStatus,
    closedAmount: payload.closedAmount ?? null,
    actualClosingDate: payload.actualClosingDate ?? null,
    closingNote: payload.closingNote ?? null,
    scheduleNext: payload.scheduleNext ?? false,
    nextDueDate: payload.nextDueDate ?? null,
    nextTitle: payload.nextTitle ?? null,
    nextDescription: payload.nextDescription ?? null,
    nextPriority: payload.nextPriority ?? null,
  });
}

export async function getSuggestedTargetedAmount(offeringIds: string[]): Promise<number> {
  const response = await apiClient.get<number>(`/leads/targeted-amount`, {
    params: { offeringIds: offeringIds.join(',') },
  });
  return response.data;
}

export async function getLeadPipeline(): Promise<LeadPipelineResponse> {
  const response = await apiClient.get<LeadPipelineResponse & { Stages?: LeadPipelineResponse['stages'] }>(
    '/metadata/leads/pipeline',
  );
  return normalizeLeadPipelineResponse(response.data);
}

export async function getLeadSources(): Promise<LeadSourceDefinition[]> {
  const response = await apiClient.get<LeadSourceDefinition[]>('/metadata/lead-sources');
  return response.data.map((source) => normalizeLeadSource(source));
}

export async function exportLeads(
  params: ExportLeadsParams = {}
): Promise<Blob> {
  const {
    search,
    pipelineStageId,
    closingStatus,
    assigneeUserId,
    contactIds,
    contactCategoryId,
    sourceId,
    campaignId,
    referralContactId,
    salesPersonId,
    createdFrom,
    createdTo,
    segmentIds,
    hasReferral,
  } = params;
  const searchParams = new URLSearchParams();
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (pipelineStageId !== undefined) searchParams.set('pipelineStageId', String(pipelineStageId));
  if (closingStatus !== undefined) searchParams.set('closingStatus', String(closingStatus));
  if (assigneeUserId !== undefined && assigneeUserId !== '')
    searchParams.set('assigneeUserId', assigneeUserId);
  if (contactIds !== undefined && contactIds.length > 0) {
    for (const id of contactIds) searchParams.append('contactIds', id);
  }
  if (contactCategoryId !== undefined) searchParams.set('contactCategoryId', String(contactCategoryId));
  if (sourceId !== undefined) searchParams.set('sourceId', String(sourceId));
  if (campaignId !== undefined && campaignId !== '') searchParams.set('campaignId', campaignId);
  if (referralContactId !== undefined && referralContactId !== '')
    searchParams.set('referralContactId', referralContactId);
  if (salesPersonId !== undefined && salesPersonId !== '')
    searchParams.set('salesPersonId', salesPersonId);
  if (createdFrom) searchParams.set('createdFrom', createdFrom);
  if (createdTo) searchParams.set('createdTo', createdTo);
  if (segmentIds?.length) {
    segmentIds.forEach((id) => searchParams.append('segmentIds', id));
  }
  if (hasReferral !== undefined) searchParams.set('hasReferral', String(hasReferral));
  const response = await apiClient.get<Blob>(`/leads/export?${searchParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function exportLeadsPdf(
  params: ExportLeadsParams = {}
): Promise<Blob> {
  const {
    search,
    pipelineStageId,
    closingStatus,
    assigneeUserId,
    contactIds,
    contactCategoryId,
    sourceId,
    campaignId,
    referralContactId,
    salesPersonId,
    createdFrom,
    createdTo,
    segmentIds,
    hasReferral,
  } = params;
  const searchParams = new URLSearchParams();
  if (search !== undefined && search !== '') searchParams.set('search', search);
  if (pipelineStageId !== undefined) searchParams.set('pipelineStageId', String(pipelineStageId));
  if (closingStatus !== undefined) searchParams.set('closingStatus', String(closingStatus));
  if (assigneeUserId !== undefined && assigneeUserId !== '')
    searchParams.set('assigneeUserId', assigneeUserId);
  if (contactIds !== undefined && contactIds.length > 0) {
    for (const id of contactIds) searchParams.append('contactIds', id);
  }
  if (contactCategoryId !== undefined) searchParams.set('contactCategoryId', String(contactCategoryId));
  if (sourceId !== undefined) searchParams.set('sourceId', String(sourceId));
  if (campaignId !== undefined && campaignId !== '') searchParams.set('campaignId', campaignId);
  if (referralContactId !== undefined && referralContactId !== '')
    searchParams.set('referralContactId', referralContactId);
  if (salesPersonId !== undefined && salesPersonId !== '')
    searchParams.set('salesPersonId', salesPersonId);
  if (createdFrom) searchParams.set('createdFrom', createdFrom);
  if (createdTo) searchParams.set('createdTo', createdTo);
  if (segmentIds?.length) {
    segmentIds.forEach((id) => searchParams.append('segmentIds', id));
  }
  if (hasReferral !== undefined) searchParams.set('hasReferral', String(hasReferral));
  const response = await apiClient.get<Blob>(`/leads/export/pdf?${searchParams.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
}

